import React, { useState } from 'react'
import { useUser } from '../../contexts/UserContext'
import DocumentTemplateManager from '../../components/document-templates/DocumentTemplateManager'
import OfficeWorkflows from './OfficeWorkflows'

type Tab = 'profile' | 'document_templates' | 'workflow_templates'

const OfficeSettings: React.FC = () => {
  const { user, loading } = useUser()
  const [tab, setTab] = useState<Tab>('profile')

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-muted-foreground">Loading office settings...</p>
        </div>
      </div>
    )
  }

  if (!user?.office) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-foreground mb-2">Office Settings</h1>
          <p className="text-muted-foreground">
            You are not currently assigned to an office.
          </p>
        </div>
      </div>
    )
  }

  const office = user.office

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-2">Office Settings</h1>
        <p className="text-muted-foreground mb-6">
          Configure settings for your office and organizational preferences
        </p>

        <div className="border-b border-border mb-6 flex items-center gap-1">
          <TabButton active={tab === 'profile'} onClick={() => setTab('profile')}>Profile</TabButton>
          <TabButton active={tab === 'document_templates'} onClick={() => setTab('document_templates')}>
            Document Templates
          </TabButton>
          <TabButton active={tab === 'workflow_templates'} onClick={() => setTab('workflow_templates')}>
            Workflow Templates
          </TabButton>
        </div>

        {tab === 'profile' && <OfficeProfileTab office={office} />}
        {tab === 'document_templates' && (
          <DocumentTemplateManager
            scope={{ level: 'office', ownerId: office.id }}
            subtitle="Customize templates for the whole office. Local edits override system defaults for anyone in this office."
          />
        )}
        {tab === 'workflow_templates' && <OfficeWorkflows />}
      </div>
    </div>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

function OfficeProfileTab({ office }: { office: any }) {
  return (
    <div className="max-w-4xl space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Office Information</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Office Name</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={office.name}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">Office Type</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
              value={office.office_type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
              disabled
            />
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Strategic Priorities</h2>
        <textarea
          className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground h-32"
          value={office.priorities || 'No strategic priorities defined'}
          placeholder="Strategic priorities and focus areas for this office..."
          disabled
        />
        <p className="text-xs text-muted-foreground mt-2 italic">
          These settings are managed by office administrators
        </p>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">Preferred Contract Structures</h2>
        <textarea
          className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground h-32"
          value={office.preferred_contract_structures || 'No preferred contract structures defined'}
          placeholder="Preferred contract types and structures (e.g., M&O, GSA FSS, IDIQ, OTA)..."
          disabled
        />
        <p className="text-xs text-muted-foreground mt-2 italic">
          These settings are managed by office administrators
        </p>
      </div>
    </div>
  )
}

export default OfficeSettings
